import { Box, FormHelperText, Switch as MuiSwitch } from '@mui/material';
import { forwardRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useEntityPermission } from '../../providers/EntityPermissionProvider/index';
import ReadOnly from './ReadOnly';

const Switch = forwardRef(
  (
    {
      overwriteProps = {},
      checkedLabel = 'Yes',
      uncheckedLabel = 'No',
      confirmDialog: ConfirmDialog = null,
      ...props
    },
    ref,
  ) => {
    const { isHidden, isReadOnly } = useEntityPermission();
    const { getValues, control } = useFormContext();
    const [pendingChange, setPendingChange] = useState(null);

    const currentValue = getValues(props?.name);

    const onConfirmDialog = () => {
      setPendingChange(current => {
        current?.apply();
        return null;
      });
    };

    const onCloseDialog = () => setPendingChange(null);

    if (isReadOnly(props?.permission, currentValue)) {
      return (
        <ReadOnly name={props?.name}>
          {currentValue ? checkedLabel : uncheckedLabel}
        </ReadOnly>
      );
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={props?.name} value={currentValue} />;
    }

    return (
      <>
        <Controller
          name={props?.name}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const { onChange: overwriteOnChange, ...restOverwriteProps } =
              overwriteProps;

            const handleChange = event => {
              const newValue = event.target.checked;

              const applyChange = () => {
                if (overwriteOnChange) {
                  overwriteOnChange(event);
                } else {
                  field.onChange(newValue);
                }
              };

              if (ConfirmDialog) {
                setPendingChange({ value: newValue, apply: applyChange });
                return;
              }

              applyChange();
            };

            return (
              <Box>
                <MuiSwitch
                  ref={ref}
                  name={field.name}
                  checked={!!field.value}
                  onBlur={field.onBlur}
                  onChange={handleChange}
                  disabled={props?.disabled || false}
                  {...restOverwriteProps}
                />
                <ReadOnly>
                  {field.value ? checkedLabel : uncheckedLabel}
                </ReadOnly>
                {error?.message && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </Box>
            );
          }}
        />
        {ConfirmDialog && (
          <ConfirmDialog
            open={Boolean(pendingChange)}
            value={pendingChange?.value}
            onConfirm={onConfirmDialog}
            onCancel={onCloseDialog}
          />
        )}
      </>
    );
  },
);

export default Switch;
